import { useEffect, useState } from "react";
import { remoteCardImageSrc, cardLocalImagePath } from "../lib/images";
import { hasLocalImage, getLocalImageSrc, writeImageFromUrl } from "../lib/offlineStore";
import { useNetworkStatus } from "./useNetworkStatus";

// Hors ligne, étape 2 : source d'image locale-d'abord. Démarre avec l'URL
// réseau habituelle (aucun changement visuel tant que le cache est vide),
// et la remplace par le fichier local si déjà synchronisé. Si l'image
// vient d'être vue en ligne et n'est pas encore en cache, la télécharge en
// arrière-plan pour la prochaine fois (cache-as-you-go, en plus de la
// synchro globale de useOfflineSync).
export function useCardImage(card, quality = "low") {
  const online = useNetworkStatus();
  const remoteSrc = card?.image ? remoteCardImageSrc(card, quality) : null;
  const [src, setSrc] = useState(remoteSrc);

  useEffect(() => {
    setSrc(remoteSrc);
    if (!remoteSrc) return;
    const path = cardLocalImagePath(card, quality);
    let cancelled = false;

    if (hasLocalImage(path)) {
      getLocalImageSrc(path).then(local => {
        if (!cancelled && local) setSrc(local);
      });
    } else {
      // Pas encore en cache pour CETTE qualité (typiquement "high" : la
      // synchro globale ne télécharge que "low", cf. useOfflineSync).
      // HORS LIGNE seulement : si l'autre qualité est déjà en cache, s'en
      // servir comme image de secours en attendant — mieux qu'un cardback.
      // EN LIGNE, ne jamais faire ce remplacement : `src` reste l'URL
      // réseau (déjà affichée ci-dessus), qui charge la vraie qualité
      // demandée — la basculer sur le cache basse qualité aurait dégradé
      // l'affichage même avec une connexion qui fonctionne (régression
      // constatée après l'ajout de ce repli).
      if (!online) {
        const fallbackPath = cardLocalImagePath(card, quality === "high" ? "low" : "high");
        if (fallbackPath !== path && hasLocalImage(fallbackPath)) {
          getLocalImageSrc(fallbackPath).then(local => {
            if (!cancelled && local) setSrc(local);
          });
        }
      }
      writeImageFromUrl(path, remoteSrc);
    }

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id, card?.image, card?.image_full, quality, online]);

  return src;
}
